import React, { useState } from 'react';
import MainLayout from '../layouts/MainLayout';
import { Card, CardContent } from '../components/Card';
import { Calendar, Clock, Tag, ArrowRight, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import { blogPosts, getFeaturedPosts, getAllCategories } from '../data/blogPosts';

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('Alle');
  const categories = ['Alle', ...getAllCategories()];
  const featuredPosts = getFeaturedPosts();

  const filteredPosts = selectedCategory === 'Alle'
    ? blogPosts
    : blogPosts.filter(post => post.category === selectedCategory);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('de-DE', options);
  };

  return (
    <MainLayout
      title="Blog | SENKAY Personal & Logistik"
      description="Aktuelles aus der Welt der Personaldienstleistungen. Tipps, Trends und Insights zu Arbeitnehmerüberlassung, Facility Management und Event-Logistik."
      keywords="Personaldienstleistungen Blog, Arbeitnehmerüberlassung, Facility Management, HR Trends"
    >
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-12 bg-gradient-to-b from-primary-skin/30 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <BookOpen className="w-16 h-16 mx-auto mb-6 text-primary-blue-mid" />
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-text-dark mb-6">
            SENKAY Blog
          </h1>
          <p className="text-lg md:text-xl text-primary-text-muted leading-relaxed">
            Expertise, Insights und Best Practices aus der Welt der Personaldienstleistungen.
            Bleiben Sie auf dem Laufenden mit aktuellen Trends und praxisnahen Tipps.
          </p>
        </div>
      </section>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary-text-dark mb-2">
              Empfohlene Artikel
            </h2>
            <p className="text-primary-text-muted">
              Unsere meistgelesenen und wichtigsten Beiträge
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredPosts.map((post) => (
              <Card key={post.id} hover={true}>
                <CardContent className="p-0">
                  {/* Image Placeholder */}
                  <div className="w-full h-48 bg-gradient-to-br from-primary-blue-light to-primary-blue-mid flex items-center justify-center">
                    <BookOpen className="w-16 h-16 text-white opacity-50" />
                  </div>

                  <div className="p-6">
                    {/* Category Badge */}
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary-blue-light text-primary-blue-mid text-xs font-semibold mb-3">
                      <Tag className="w-3 h-3 mr-1" />
                      {post.category}
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-primary-text-dark mb-3 line-clamp-2">
                      {post.title}
                    </h3>

                    {/* Excerpt */}
                    <p className="text-sm text-primary-text-muted mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>

                    {/* Meta Info */}
                    <div className="flex items-center justify-between text-xs text-primary-text-muted mb-4">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate(post.date)}
                      </div>
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {post.readTime}
                      </div>
                    </div>

                    {/* Read More Link */}
                    <Link to={`/blog/${post.slug}`}>
                      <Button variant="outline" size="sm" icon={ArrowRight} iconPosition="right" className="w-full">
                        Weiterlesen
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 border-t border-primary-gray-border">
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-primary-text-dark mb-6">
            Alle Artikel
          </h2>

          {/* Filter Buttons */}
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-primary-blue-mid text-white shadow-md'
                    : 'bg-white text-primary-text-dark border border-primary-gray-border hover:border-primary-blue-mid'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* All Posts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPosts.map((post) => (
            <Card key={post.id} hover={true}>
              <CardContent className="p-0">
                {/* Image Placeholder */}
                <div className="w-full h-48 bg-gradient-to-br from-primary-skin to-primary-blue-light flex items-center justify-center">
                  <BookOpen className="w-16 h-16 text-primary-blue-mid opacity-30" />
                </div>

                <div className="p-6">
                  {/* Category Badge */}
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary-skin text-primary-text-dark text-xs font-semibold mb-3">
                    <Tag className="w-3 h-3 mr-1" />
                    {post.category}
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-primary-text-dark mb-3 line-clamp-2">
                    {post.title}
                  </h3>

                  {/* Excerpt */}
                  <p className="text-sm text-primary-text-muted mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  {/* Author & Meta Info */}
                  <div className="flex items-center justify-between text-xs text-primary-text-muted mb-4 pb-4 border-b border-primary-gray-border">
                    <div>
                      <div className="font-semibold text-primary-text-dark">{post.author}</div>
                      <div>{post.authorRole}</div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center justify-end mb-1">
                        <Calendar className="w-3 h-3 mr-1" />
                        {formatDate(post.date)}
                      </div>
                      <div className="flex items-center justify-end">
                        <Clock className="w-3 h-3 mr-1" />
                        {post.readTime}
                      </div>
                    </div>
                  </div>

                  {/* Read More Link */}
                  <Link to={`/blog/${post.slug}`}>
                    <Button variant="outline" size="sm" icon={ArrowRight} iconPosition="right" className="w-full">
                      Weiterlesen
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* No Results Message */}
        {filteredPosts.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-primary-text-muted opacity-50" />
            <h3 className="text-xl font-semibold text-primary-text-dark mb-2">
              Keine Artikel gefunden
            </h3>
            <p className="text-primary-text-muted">
              In dieser Kategorie sind aktuell keine Artikel verfügbar.
            </p>
          </div>
        )}
      </section>

      {/* Newsletter CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Card>
          <CardContent className="text-center p-8 md:p-12 bg-gradient-to-br from-primary-blue-light to-primary-skin">
            <h2 className="text-2xl md:text-3xl font-bold text-primary-text-dark mb-4">
              Bleiben Sie informiert
            </h2>
            <p className="text-primary-text-muted mb-8 max-w-2xl mx-auto">
              Verpassen Sie keine Insights mehr. Abonnieren Sie unseren Newsletter und erhalten
              Sie regelmäßig Tipps, Trends und Best Practices direkt in Ihr Postfach.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <Link to="/kontakt" className="flex-1">
                <Button variant="primary" size="lg" className="w-full">
                  Newsletter abonnieren
                </Button>
              </Link>
              <Link to="/kontakt" className="flex-1">
                <Button variant="outline" size="lg" className="w-full">
                  Kontakt aufnehmen
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </section>
    </MainLayout>
  );
};

export default BlogPage;
